import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import {
  todayLocal,
  firstOfMonthLocal,
  formatLocal,
  formatLocalDate,
  getDeviceTimeZone,
} from '@/utils/dateUtils';

/**
 * Shared hook for report pages.
 * Date filters use the viewer's local calendar day.
 * Timestamps are shown in the viewer's device timezone (UTC → local).
 */
export function useReportFilters() {
  const [fromDate, setFromDate] = useState(() => firstOfMonthLocal());
  const [toDate, setToDate] = useState(() => todayLocal());
  const [todayDate, setTodayDate] = useState(() => todayLocal());
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [departments, setDepartments] = useState([]);
  // Device TZ for UI labels — not used to convert API timestamps specially
  const [timezone, setTimezone] = useState(getDeviceTimeZone());

  useEffect(() => {
    const load = async () => {
      try {
        const [staffRes, settingsRes] = await Promise.all([
          axios.get(`${API}/admin/staff`, { withCredentials: true }),
          axios.get(`${API}/admin/settings/company`, { withCredentials: true }),
        ]);
        setStaffList(staffRes.data || []);
        const depts = [
          ...new Set(
            (staffRes.data || []).map((s) => s.department).filter(Boolean)
          ),
        ];
        setDepartments(depts);

        // Prefer device TZ for display labels; company TZ is settings-only.
        setTimezone(getDeviceTimeZone() || settingsRes.data?.timezone || 'UTC');

        const correctToday = todayLocal();
        const correctFirstOfMonth = firstOfMonthLocal();
        setTodayDate(correctToday);
        setToDate(correctToday);
        setFromDate(correctFirstOfMonth);
      } catch (e) {
        console.error('Failed to load filters:', e);
        const localToday = todayLocal();
        setTodayDate(localToday);
        setToDate(localToday);
        setFromDate(firstOfMonthLocal());
      }
    };
    load();
  }, []);

  const setPreset = (preset) => {
    const now = new Date();
    if (preset === 'today') {
      const d = todayLocal();
      setFromDate(d);
      setToDate(d);
    } else if (preset === 'week') {
      const day = now.getDay();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
      setFromDate(formatLocalDateISOSafe(start));
      setToDate(todayLocal());
    } else if (preset === 'month') {
      setFromDate(firstOfMonthLocal());
      setToDate(todayLocal());
    } else if (preset === 'last_month') {
      const y = now.getFullYear();
      const m = now.getMonth(); // 0-based
      const prevYear = m === 0 ? y - 1 : y;
      const prevMonthIndex = m === 0 ? 11 : m - 1;
      const lastDay = new Date(prevYear, prevMonthIndex + 1, 0).getDate();
      const pm = String(prevMonthIndex + 1).padStart(2, '0');
      setFromDate(`${prevYear}-${pm}-01`);
      setToDate(`${prevYear}-${pm}-${String(lastDay).padStart(2, '0')}`);
    }
  };

  /** Format UTC ISO → device local time (name kept for report pages). */
  const formatTZ = (isoStr, opts = {}) => {
    if (!isoStr) return '-';
    return formatLocal(isoStr, {
      hour: '2-digit',
      minute: '2-digit',
      ...opts,
    });
  };

  const formatDateTZ = (isoStr) => formatLocalDate(isoStr);

  const formatMinutes = (minutes) => {
    if (!minutes && minutes !== 0) return '-';
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return '-';
    return `${hours.toFixed(1)}h`;
  };

  return {
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    todayDate,
    selectedStaff,
    setSelectedStaff,
    selectedDepartment,
    setSelectedDepartment,
    staffList,
    departments,
    timezone,
    setPreset,
    formatTZ,
    formatDateTZ,
    formatMinutes,
    formatHours,
  };
}

function formatLocalDateISOSafe(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
